/**
 * ZONIY - Vendor Layout Component
 * @module VendorLayout
 * @author Osman - Frontend Developer
 * @description Vendor portal layout with sidebar navigation
 * @version 1.0.0
 * Git: Osman <osman@linesoft.com> - Vendor layout
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, FileText, RefreshCw, 
  ChevronLeft, Menu, LogOut, Search, Home 
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const VendorLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const navigation = [
    { name: 'Dashboard', href: '/vendor', icon: LayoutDashboard },
    { name: 'Inventory', href: '/vendor/inventory', icon: Package },
    { name: 'Orders', href: '/vendor/orders', icon: ShoppingCart },
    { name: 'Reports', href: '/vendor/reports', icon: FileText },
    { name: 'MVS Sync', href: '/vendor/sync', icon: RefreshCw },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white flex-shrink-0 transition-all duration-300`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
            {sidebarOpen && (
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 flex items-center justify-center">
                  <span className="font-heading font-black text-sm">Z</span>
                </div>
                <span className="font-heading font-bold text-lg">VENDOR</span>
              </Link>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-slate-800 rounded"
              data-testid="toggle-sidebar"
            >
              {sidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                    isActive 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="font-medium">{item.name}</span>}
                </Link>
              );
            })}
          </nav>

          {/* Bottom Actions */}
          <div className="border-t border-slate-800 py-4">
            <Link
              to="/search"
              className="flex items-center gap-3 px-4 py-3 text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
            >
              <Search className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>Search Parts</span>}
            </Link>
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
            >
              <Home className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>Marketplace</span>}
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-slate-800 hover:text-red-300 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>Logout</span>}
            </button>
          </div>

          {/* User Info */}
          {sidebarOpen && user && (
            <div className="px-4 py-4 border-t border-slate-800">
              <p className="font-medium text-white truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">{user.company_name || user.email}</p>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default VendorLayout;
